import React, { useCallback, useMemo } from 'react';
import { Dropdown, Modal } from 'antd';
import { LeftOutlined } from '@ant-design/icons';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { Button } from 'components/elements';
import { BackButton } from 'containers/singleGroup/index.styled';
import { useGetMyAccountQuery } from 'graphql/types';
import {
  DecisionModalParagraph,
  RequestsEmptyState,
  RequestsPageTitle,
  RequestsPanel,
  RequestsToolbarActions,
  RequestsToolbarRow,
  RequestsList,
  RequestsListItem,
  StyledButtonText,
  StyledWrapper,
} from './settings.styled';
import { useFamilyShareItems } from './useFamilyShareItems';

type MatchParams = { id: string };
type FamilyTreeRequestsProps = RouteComponentProps<MatchParams>;

export default function FamilyTreeRequests(props: FamilyTreeRequestsProps) {
  const {
    match: {
      params: { id: nodeId },
    },
  } = props;

  const history = useHistory();
  const { data: accountResult, loading: accountLoading } = useGetMyAccountQuery();
  const userInfo = useMemo(() => accountResult?.myAccount?.data?.attributes || null, [accountResult]);

  const {
    shareItems,
    shareLoading,
    selectedShare,
    selectedShareLabel,
    shareMenu,
    actionInfo,
    decisionModalOpen,
    setDecisionModalOpen,
    selectedRequest,
    handleOpenDecisionModal,
    handleAcceptShare,
    handleDeclineShare,
    handleCancelShare,
    handleImportShare,
  } = useFamilyShareItems(userInfo);

  const handlePageBack = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push(`/account/family-tree/${nodeId}`);
  }, [history, nodeId]);

  return (
    <StyledWrapper>
      <RequestsPanel>
        <BackButton type="button" onClick={handlePageBack}>
          <LeftOutlined />
          Back
        </BackButton>
        <RequestsPageTitle>Sharing Requests</RequestsPageTitle>

        {!shareLoading && shareItems.length === 0 && (
          <RequestsEmptyState>No sharing requests yet.</RequestsEmptyState>
        )}

        {!shareLoading && shareItems.length > 0 && (
          <RequestsToolbarRow>
            <RequestsToolbarActions>
              <Dropdown menu={shareMenu} trigger={['click']}>
                <Button type="primary">{selectedShareLabel}</Button>
              </Dropdown>

              {selectedShare &&
                (selectedShare.type === 'incoming' ? (
                  <Button type="default" disabled={actionInfo.disabled} onClick={handleOpenDecisionModal}>
                    <StyledButtonText>{actionInfo.text}</StyledButtonText>
                  </Button>
                ) : selectedShare.status === 'accepted' ? (
                  <Button
                    type="default"
                    disabled={actionInfo.disabled}
                    onClick={() => handleImportShare(selectedShare, nodeId)}
                  >
                    <StyledButtonText>{actionInfo.text}</StyledButtonText>
                  </Button>
                ) : (
                  <Button
                    type="default"
                    disabled={actionInfo.disabled}
                    onClick={() => handleCancelShare(selectedShare)}
                  >
                    <StyledButtonText>{actionInfo.text}</StyledButtonText>
                  </Button>
                ))}
            </RequestsToolbarActions>
          </RequestsToolbarRow>
        )}

        {!accountLoading && !shareLoading && shareItems.length > 0 && (
          <RequestsList>
            {shareItems.map((item) => (
              <RequestsListItem key={item.key}>
                <strong>{item.partnerName}</strong> — {item.type === 'outgoing' ? 'Sent' : 'Received'} ({item.status})
              </RequestsListItem>
            ))}
          </RequestsList>
        )}
      </RequestsPanel>

      <Modal
        title="Respond to Sharing Request"
        open={decisionModalOpen}
        onCancel={() => setDecisionModalOpen(false)}
        footer={[
          <Button key="decline" type="primary" danger onClick={handleDeclineShare}>
            Decline
          </Button>,
          <Button key="accept" type="primary" onClick={handleAcceptShare}>
            Accept
          </Button>,
          <Button key="cancel" type="default" onClick={() => setDecisionModalOpen(false)}>
            Close
          </Button>,
        ]}
        destroyOnClose={false}
        maskClosable
      >
        <DecisionModalParagraph>
          {selectedRequest?.senderName
            ? `Accept sharing request from ${selectedRequest.senderName}?`
            : 'Accept sharing request?'}
        </DecisionModalParagraph>
      </Modal>
    </StyledWrapper>
  );
}
